import {
  Box,
  Center,
  Container,
  Heading,
  Link,
  Stack,
  useCallbackRef,
  useMediaQuery,
} from '@chakra-ui/react';
import { GoogleMap, Libraries, useJsApiLoader } from '@react-google-maps/api';
import InputSelectGroup from 'components/inputs/InputSelectGroup';
import PageHeader from 'components/page-header/PageHeader';
import PageTitleMobile from 'components/page-header/PageTitleMobile';
import AgentItem from 'components/pages-agent/AgentItem';
import AgentItemMarker from 'components/pages-agent/AgentItemMarker';
import { IQueryFindManyDto } from 'dtos/query.dto';
import useDealer from 'hooks/useDealer';
import useProvince from 'hooks/useProvince';
import {
  IDealer,
  IDealerRegion,
  IDealerRegionProvince,
  IDealerShop,
} from 'interfaces/dealer.interface';
import { ThailandRegionEnum } from 'interfaces/provice.interface';
import { withIronSessionSsr } from 'iron-session/next';
import RootLayout from 'layouts/RootLayout';
import { sessionOptions } from 'libs/iron-session';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import queryString from 'query-string';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useSWR from 'swr';
import { NextPageWithLayout } from './_app';
import { IPaginationResponse } from 'dtos/pagination.dto';
import { Swiper, SwiperSlide } from 'swiper/react';
import Image from 'next/image';
import { Pagination } from 'swiper';

const center = {
  lat: 13.736717,
  lng: 100.523186,
};

const libraries: Libraries = ['places', 'marker', 'maps'];

const AgentPage: NextPageWithLayout = () => {
  const [isTablet] = useMediaQuery('(min-width: 768px)');

  const [isDesktop] = useMediaQuery('(min-width: 1024px)');

  const { control, watch, setValue } = useForm<any>({
    defaultValues: {
      region: ThailandRegionEnum.BANGKOK_METROPOLIS,
      province: '1',
    },
  });

  const [queryObject, setQueryObject] = useState<IQueryFindManyDto>({
    page: 1,
    limit: 1000,
    include: ['province'],
    where: undefined,
  });

  const {
    data: dealersData,
    dataLoading: dealersDataLoading,
    dataMutate,
  } = useDealer({
    revalidateOnFocus: true,
    queryStringify: queryString.stringify(queryObject),
  });

  const { data: dealerProvinces } = useSWR<IDealerRegion[]>(
    `${process.env.NEXT_PUBLIC_API_URL}/v1/public/dealers/provinces`
  );

  console.log('dealerProvinces', dealerProvinces);

  const { provinces, region: regions } = useProvince();

  const { data: dealerShopData } = useSWR<IPaginationResponse<IDealerShop>>(
    `${process.env.NEXT_PUBLIC_API_URL}/v1/public/dealers/shops`
  );

  console.log('dealerShopData', dealerShopData);

  const regionFormValues = watch('region');

  const provinceFormValues = watch('province');

  const [currentDealer, setCurrentDealer] = useState<IDealer | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyBhtP1-PVOxLuSLwvat57bBpchG2a2CCCM',
    libraries: libraries,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);

  const [currentRegionProvinces, setCurrentRegionProvinces] = useState<
    IDealerRegionProvince[]
  >([]);

  const onLoad = useCallback(function callback(map: google.maps.Map) {
    map.setZoom(14);

    setMap(map);
  }, []);

  const onUnmount = useCallbackRef(function callback(map) {
    setMap(null);
  }, []);

  useEffect(() => {
    if (provinces && regionFormValues && map) {
      const _currentRegion = dealerProvinces?.find(
        (province) => province.region === regionFormValues
      );

      setCurrentRegionProvinces(_currentRegion?.provinces || []);

      if (_currentRegion && _currentRegion.provinces.length > 0) {
        setValue('province', _currentRegion.provinces[0].id.toString());
      } else {
        setValue('province', '');
      }
    }
  }, [dealerProvinces, regionFormValues, setValue, map]);

  useEffect(() => {
    if (provinceFormValues && currentRegionProvinces) {
      setQueryObject((prev) => ({
        ...prev,
        where: 'provinceId|in|' + provinceFormValues,
      }));
    } else {
      setQueryObject((prev) => ({
        ...prev,
        where: undefined,
      }));
    }
  }, [provinceFormValues, map]);

  useEffect(() => {
    if (map && dealersData && dealersData.docs.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      dealersData.docs.forEach((dealer) => {
        if (dealer.latitude && dealer.longitude) {
          bounds.extend({
            lat: dealer.latitude,
            lng: dealer.longitude,
          });
        }
      });

      map.fitBounds(bounds);
    }
  }, [map, dealersData]);

  return (
    <>
      <Head>
        <title>ตัวแทนจำหน่าย | แลมป์ตั้น</title>
      </Head>
      <Box>
        <PageHeader title={`ตัวแทนจำหน่าย`}></PageHeader>
        <PageTitleMobile>{`ตัวแทนจำหน่าย`}</PageTitleMobile>
        <Container
          pt={{
            base: 8,
            md: 12,
          }}
          pb={{
            base: 8,
            md: 20,
          }}
        >
          {dealerShopData && dealerShopData.docs.length > 0 ? (
            <Box>
              <Heading color="primary.500" mb={4} fontWeight={400}>
                ห้างสรรพสินค้า
              </Heading>
              <Box
                mx={{
                  base: -4,
                  md: 0,
                }}
              >
                <Box
                  mb={6}
                  p={4}
                  pr={{
                    base: 0,
                    md: 4,
                  }}
                  pb={8}
                  bg="#EADFD0"
                  position="relative"
                >
                  <Swiper
                    spaceBetween={10}
                    slidesPerView={isDesktop ? 7 : isTablet ? 4.2 : 3.2}
                    onSlideChange={() => console.log('slide change')}
                    onSwiper={(swiper) => console.log(swiper)}
                    centeredSlides={false}
                    pagination={{
                      clickable: true,
                      el: '.my-swiper-pagination',
                    }}
                    modules={[Pagination]}
                    className="dealer-shop-swiper"
                  >
                    {dealerShopData.docs.map((shop) => (
                      <SwiperSlide key={shop.id}>
                        <Center boxSize="100px">
                          {shop.websiteUrl ? (
                            <Link href={`${shop.websiteUrl}`} target="_blank">
                              <Image
                                src={`${process.env.NEXT_PUBLIC_FILE_URL}/${shop.logoPath}`}
                                width={100}
                                height={100}
                                alt={shop.name}
                              />
                            </Link>
                          ) : (
                            <Image
                              src={`${process.env.NEXT_PUBLIC_FILE_URL}/${shop.logoPath}`}
                              width={100}
                              height={100}
                              alt={shop.name}
                            />
                          )}
                        </Center>
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  <Box
                    position="absolute"
                    bottom={2}
                    left="50% !important"
                    transform="translateX(-50%)"
                    zIndex="1"
                    columnGap={1}
                    display="flex"
                    width="auto !important"
                    className="my-swiper-pagination"
                  ></Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <></>
          )}
          <Heading color="primary.500" mb={4} fontWeight={400}>
            ร้านค้าตัวแทนจำหน่าย
          </Heading>
          <Box position="relative">
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{
                  width: '100%',
                  height: isDesktop ? '600px' : isTablet ? '400px' : '300px',
                }}
                center={center}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                  mapTypeControl: false,
                  streetViewControl: false,
                  gestureHandling: 'greedy',
                }}
              >
                {/* Child components, such as markers, info windows, etc. */}
                <>
                  {map && dealersData && dealersData.docs.length > 0 ? (
                    dealersData.docs.map((dealer) => {
                      return (
                        <AgentItemMarker
                          key={dealer.id}
                          dealer={dealer}
                          onClick={() => {
                            map.setCenter({
                              lat: dealer.latitude,
                              lng: dealer.longitude,
                            });
                            map.setZoom(14);
                          }}
                          showInfoWindow={currentDealer?.id === dealer.id}
                          onInfoWindowClose={() => {
                            setCurrentDealer(null);
                          }}
                        ></AgentItemMarker>
                      );
                    })
                  ) : (
                    <></>
                  )}
                </>
              </GoogleMap>
            ) : (
              <></>
            )}

            {isLoaded && (
              <Box
                position={{
                  lg: 'absolute',
                }}
                top="0"
                left="0"
                height="100%"
                boxShadow="md"
                mt={{
                  base: 6,
                  lg: '0',
                }}
                p={{
                  lg: 4,
                }}
              >
                <Box
                  bg="white"
                  width={{
                    base: '100%',
                    lg: '300px',
                  }}
                  display="flex"
                  flexDirection="column"
                  height="100%"
                >
                  <Stack bg="#EADED0" p={4} flex="0 0 auto">
                    <Box>
                      <InputSelectGroup
                        name="region"
                        control={control}
                        rules={{
                          required: 'กรุณาเลือกภูมิภาค',
                        }}
                        bg="white"
                        options={
                          dealerProvinces
                            ? dealerProvinces.map((item) => ({
                                value: item.region,
                                label:
                                  regions.find(
                                    (region) => region.value === item.region
                                  )?.name || '',
                              }))
                            : []
                        }
                      />
                    </Box>
                    <Box>
                      <InputSelectGroup
                        name="province"
                        control={control}
                        rules={{
                          required: 'กรุณาเลือกจังหวัด',
                        }}
                        bg="white"
                        options={
                          currentRegionProvinces
                            ? currentRegionProvinces.map((province) => ({
                                value: province.id.toString(),
                                label: province.name_th,
                              }))
                            : [
                                {
                                  value: '',
                                  label: '',
                                },
                              ]
                        }
                      />
                    </Box>
                  </Stack>
                  <Box p={4} flex="1" overflowY="auto">
                    {dealersData?.docs && dealersData.docs.length > 0 ? (
                      dealersData.docs.map((dealer) => (
                        <AgentItem
                          key={dealer.id}
                          dealer={dealer}
                          onClick={() => {
                            map?.setCenter({
                              lat: dealer.latitude,
                              lng: dealer.longitude,
                            });
                            map?.setZoom(14);
                            setCurrentDealer(dealer);
                          }}
                        ></AgentItem>
                      ))
                    ) : (
                      <Box>ไม่พบตัวแทนจำหน่าย</Box>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </>
  );
};

AgentPage.getLayout = (page) => {
  return <RootLayout>{page}</RootLayout>;
};

export const getServerSideProps = withIronSessionSsr(async function ({
  req,
  res,
  locale,
}) {
  const _locale = locale || 'th';

  return {
    props: {
      ...(await serverSideTranslations(_locale, ['common'])),
    },
  };
},
sessionOptions);

export default AgentPage;
